// Canal de mise à jour de l'agent : « stable » (tout le parc) ou un des trois
// anneaux pilotes, dans l'ordre où une nouvelle version les traverse :
// Pilote 1 (le plus précoce) ▸ Pilote 2 ▸ Pilote 3 ▸ stable. Une publication
// d'agent redémarre ~450 services chez les clients : elle part sur quelques
// machines choisies ici, et n'est promue au parc qu'une fois validée.
// Règle de résolution côté serveur (Services/AgentUpdateManifest.cs) : un
// appareil reçoit la plus récente des versions de son anneau, des anneaux
// suivants et du stable — `/devices/agent-versions` la donne toute calculée.
//
// Exports :
// - PilotBadge : la pastille, pour les listes (tableau de bord) ;
// - UpdateChannelControl : version + canal, pour la fiche appareil ;
// - CHANNELS / channelLabel / isPilot : libellés partagés.

import { useEffect, useState } from 'react';
import { FlaskConical } from 'lucide-react';
import { api } from '../lib/api.jsx';

export const STABLE = 'stable';
/** Canaux dans l'ordre d'affichage : stable d'abord, puis les anneaux du plus précoce au plus tardif. */
export const CHANNELS = [
  [STABLE, 'Stable'],
  ['pilot1', 'Pilote 1'],
  ['pilot2', 'Pilote 2'],
  ['pilot3', 'Pilote 3'],
];
// « pilot » = ancien canal pilote unique, devenu Pilote 1 (migré en base au démarrage).
const normalize = (c) => (c === 'pilot' ? 'pilot1' : c || STABLE);
export const normalizeChannel = normalize;
export const isPilot = (c) => normalize(c) !== STABLE;
export const channelLabel = (c) => (CHANNELS.find(([v]) => v === normalize(c)) || [null, c])[1];

export function PilotBadge({ channel }) {
  return (
    <span
      title="Anneau pilote : reçoit les nouvelles versions d'agent avant le reste du parc (Pilote 1 en premier)"
      // Ambre écrit en clair puis `dark:` : même convention que MachineTypeBadge,
      // amber-300 sur blanc n'a pas assez de contraste en thème clair.
      className="inline-flex items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300"
    >
      <FlaskConical size={10} strokeWidth={2} />
      {channelLabel(channel)}
    </span>
  );
}

// Clé d'architecture du manifeste (cf. Services/AgentUpdateManifest.cs) déduite
// de l'inventaire : « ARM 64-bit Processor », « aarch64 »… → arm64.
function archKey(platform, osArchitecture) {
  const arm = /arm|aarch/i.test(osArchitecture || '');
  return (platform === 'linux' ? 'linux-' : '') + (arm ? 'arm64' : 'x64');
}

// Une seule requête par session : le manifeste ne change qu'à une publication.
let versionsPromise = null;
function loadVersions() {
  if (!versionsPromise) {
    versionsPromise = api.get('/devices/agent-versions').then((r) => {
      if (!r) versionsPromise = null; // échec : on retentera à la prochaine fiche
      return r;
    });
  }
  return versionsPromise;
}

export function UpdateChannelControl({ agent, osArchitecture, canManage, onChanged }) {
  const [versions, setVersions] = useState(null);
  const [saving, setSaving] = useState(false);
  const channel = normalize(agent.updateChannel);

  useEffect(() => {
    let alive = true;
    loadVersions().then((r) => alive && setVersions(r));
    return () => {
      alive = false;
    };
  }, []);

  const v = versions?.[archKey(agent.platform, osArchitecture)];
  const receives = (c) => v?.receives?.[c] || null;
  const stable = receives(STABLE);
  const ahead = (c) => {
    const r = receives(c);
    return r && stable && cmpVersions(r, stable) > 0 ? r : null;
  };

  const change = async (next) => {
    if (next === channel) return;
    const nextAhead = ahead(next);
    const msg =
      next === STABLE
        ? "Remettre cet appareil en stable ?\n\nIl ne recevra plus les versions pilotes. Une version pilote déjà installée reste en place (l'agent ne redescend jamais de version) jusqu'à ce que le stable la rattrape."
        : `Placer cet appareil dans « ${channelLabel(next)} » ?\n\nIl recevra les nouvelles versions de l'agent avant le reste du parc, au prochain contrôle de mise à jour (moins d'une heure).` +
          (nextAhead ? `\n\nVersion qu'il recevra : ${nextAhead}.` : '');
    if (!window.confirm(msg)) return;
    setSaving(true);
    const r = await api.put('/devices/' + agent.id + '/update-channel', { channel: next });
    setSaving(false);
    if (r) onChanged(r.updateChannel);
    else alert('Échec du changement de canal.');
  };

  let note = null;
  if (isPilot(channel) && v) {
    const a = ahead(channel);
    note = a
      ? `Reçoit ${a} ; le parc reste en ${stable}.`
      : `Aucune version en avance sur cet anneau : suit le stable${stable ? ` (${stable})` : ''}.`;
  }

  return (
    // Même gabarit que KV dans DeviceDetail : la cellule s'aligne sur ses voisines.
    <div className="text-xs">
      <div className="text-muted-2 text-[11px] mb-0.5">Version Agent</div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-fg font-mono text-[12.5px]">{agent.agentVersion || '—'}</span>
        {canManage ? (
          <select
            value={channel}
            disabled={saving}
            onChange={(e) => change(e.target.value)}
            title="Canal de mise à jour de l'agent"
            className="bg-card border border-rule rounded px-1.5 py-0.5 text-[11px] text-fg cursor-pointer disabled:opacity-50"
          >
            {CHANNELS.map(([c, l]) => (
              <option key={c} value={c}>
                {l}
                {ahead(c) ? ` — ${ahead(c)}` : ''}
              </option>
            ))}
          </select>
        ) : (
          isPilot(channel) && <PilotBadge channel={channel} />
        )}
      </div>
      {note && <div className="text-muted-2 text-[11px] mt-0.5">{note}</div>}
    </div>
  );
}

// Même règle que AgentUpdateManifest.CompareVersions côté serveur.
function cmpVersions(a, b) {
  const pa = String(a).split('.').map((n) => parseInt(n, 10) || 0);
  const pb = String(b).split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d) return d;
  }
  return 0;
}
